/**
 * Phase-1 packaging — per-customer Plan & Entitlements management panel.
 *
 * Platform Admin can: pick an edition preset, toggle individual modules (with
 * auto-added prerequisites previewed via the backend /validate endpoint), flip
 * Workflow Protection between enforce/observe, and set the non-functional Service
 * Plan (support / availability / DR). Reads /entitlements summary on open.
 *
 * Mirrors the gating of the rest of the Customers section: writes are Platform
 * Admin only (the backend is authoritative; this is the UX surface).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ShieldCheck, AlertTriangle, Check } from 'lucide-react';
import { Slideout } from './Slideout';
import { Button } from './Button';
import { Pill } from './Pill';
import { Card } from './Card';
import { RateCardCard } from './RateCardCard';
import { UsageCard } from './UsageCard';
import { OrdersCard } from './OrdersCard';
import * as api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

const MODULES = [
  { key: 'WORKFLOW_ORCH', label: 'M1 · Workflow Orchestration' },
  { key: 'WORKFLOW_PROTECT', label: 'M2 · Workflow Protection' },
  { key: 'GOVERNANCE', label: 'M3 · Protection Layer' },
  { key: 'AGENT_GOV', label: 'M4 · Agent Protection' },
  { key: 'EXT_AGENT_GOV', label: 'M5 · External Agent Protection' },
  { key: 'SHADOW_DISCOVERY', label: 'M6 · Shadow AI Discovery' },
  { key: 'TRUST_COMPLIANCE', label: 'M7 · Trust & Compliance Center' },
  { key: 'IDENTITY_AUTHZ', label: 'M8 · Identity & Authorization' },
  { key: 'API_GATEWAY', label: 'M9 · Protected Gateway & API Access' },
  { key: 'PROVE7_CONTAINER', label: 'M10 · Prove7 Standalone Container' },
];

const EDITIONS = [
  { key: 'GOVERNED_WORKFLOW', label: 'Protected Workflow', modules: ['WORKFLOW_ORCH', 'WORKFLOW_PROTECT', 'GOVERNANCE'] },
  { key: 'AGENTIC_PACK', label: 'Agentic Pack', modules: ['WORKFLOW_ORCH', 'WORKFLOW_PROTECT', 'GOVERNANCE', 'AGENT_GOV', 'TRUST_COMPLIANCE', 'IDENTITY_AUTHZ'] },
  { key: 'AGENTIC_GOVERNANCE', label: 'Agentic Protection', modules: ['WORKFLOW_ORCH', 'WORKFLOW_PROTECT', 'GOVERNANCE', 'AGENT_GOV', 'EXT_AGENT_GOV', 'IDENTITY_AUTHZ', 'API_GATEWAY'] },
  { key: 'CONTINUOUS_TRUST', label: 'Continuous Trust', modules: MODULES.map((m) => m.key) },
];

const SUPPORT = ['STANDARD', 'PREMIUM', 'PREMIER'];
const AVAIL = ['AVAIL_999', 'AVAIL_9995', 'AVAIL_9999'];
const DR = ['SINGLE_REGION', 'ACTIVE_STANDBY', 'ACTIVE_ACTIVE'];

// Sellable subscription quotas (QuotaKey). Soft = warn, Hard = block/meter; blank = unlimited.
const QUOTAS = [
  { key: 'WORKFLOW_EXECUTIONS', label: 'Workflow executions', unit: '/ month' },
  { key: 'CONCURRENT_WORKFLOWS', label: 'Concurrent workflows', unit: 'count' },
  { key: 'ACTIONS_PER_SEC', label: 'Throughput', unit: '/ sec' },
  { key: 'CONNECTORS', label: 'Connectors', unit: 'count' },
  { key: 'TENANTS_INSTANCES', label: 'Tenants / instances', unit: 'count' },
  { key: 'REGIONS', label: 'Regions', unit: 'count' },
  // Managed-model monthly spend budget, in whole USD. Soft = warn (never blocks); bring-your-own-key
  // spend is billed by the provider and never counted here. Enter e.g. 500 for $500 / month.
  { key: 'LLM_COST_USD', label: 'Managed LLM budget', unit: '$ / month' },
];

// Accept big-number shorthand so operators can type 1m / 2.5b instead of counting zeros.
const parseQty = (raw) => {
  if (raw == null) return null;
  const s = String(raw).trim().replace(/[,_\s]/g, '');
  if (s === '') return null;
  const m = s.match(/^(\d+(?:\.\d+)?)([kmbt]?)$/i);
  if (!m) return null;
  const mult = { k: 1e3, m: 1e6, b: 1e9, t: 1e12 }[m[2].toLowerCase()] || 1;
  return Math.round(parseFloat(m[1]) * mult);
};
const fmtQty = (n) => (n == null ? '' : Number(n).toLocaleString());
const usedShorthand = (raw) => raw != null && /[kmbt]/i.test(String(raw));

const STATE_TONE = { ENTITLED: 'success', OBSERVE_ONLY: 'action', READ_ONLY: 'warning', DISABLED: 'muted' };
const selCls = 'h-9 px-2.5 rounded-v2-sm border border-v2-border-default bg-v2-surface text-[13px] text-v2-ink focus:outline-none focus:border-v2-action';

export function EntitlementsPanel({ isOpen, onClose, customer, onChanged }) {
  const { addToast } = useToast();
  const cid = customer?.id;
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [protection, setProtection] = useState('OBSERVE_ONLY');
  const [autoAdded, setAutoAdded] = useState([]);
  const [plan, setPlan] = useState({ supportTier: 'STANDARD', availabilityTier: 'AVAIL_999', drTopology: 'SINGLE_REGION', regions: 1 });
  const [enforcementMode, setEnforcementMode] = useState('OBSERVE');
  const [billing, setBilling] = useState(null);
  const [billingSaving, setBillingSaving] = useState(false);
  const [quotaEdits, setQuotaEdits] = useState({});
  const [billingModes, setBillingModes] = useState({}); // provider → 'MANAGED' | 'BYOK'
  const [billingModeSaving, setBillingModeSaving] = useState(false);

  const load = async () => {
    if (!cid) return;
    setLoading(true);
    try {
      const res = await api.getEntitlements(cid);
      const data = res && res.data ? res.data : res;
      setSummary(data);
      const usable = new Set((data?.usable || []).filter((k) => k !== 'CORE'));
      setSelected(usable);
      setProtection(data?.modules?.WORKFLOW_PROTECT === 'ENTITLED' ? 'ENTITLED' : 'OBSERVE_ONLY');
      if (data?.servicePlan) setPlan({ ...plan, ...data.servicePlan });
      setEnforcementMode(data?.enforcementMode || 'OBSERVE');
    } catch (e) {
      addToast({ tone: 'danger', message: e?.message || 'Failed to load plan' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isOpen) load(); /* eslint-disable-next-line */ }, [isOpen, cid]);

  // Billing config loads alongside the plan (additive; best-effort).
  useEffect(() => {
    if (!isOpen || !cid) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getBillingConfig(cid);
        if (!cancelled) setBilling(res && res.data ? res.data : res);
      } catch (_) { /* card falls back to defaults */ }
    })();
    return () => { cancelled = true; };
  }, [isOpen, cid]);

  // Contractual LLM billing mode per provider (best-effort; defaults to Managed).
  useEffect(() => {
    if (!isOpen || !cid) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getLlmBillingModes(cid);
        if (!cancelled) setBillingModes((res && res.data ? res.data : res) || {});
      } catch (_) { /* card falls back to Managed */ }
    })();
    return () => { cancelled = true; };
  }, [isOpen, cid]);

  const saveBillingMode = async (provider, mode) => {
    setBillingModeSaving(true);
    try {
      const res = await api.setLlmBillingMode(cid, provider, mode);
      setBillingModes((res && res.data ? res.data : res) || {});
      addToast({ tone: 'success', message: `Anthropic billing set to ${mode === 'BYOK' ? 'Bring Your Own Key (BYOK)' : 'Managed'}` });
    } catch (e) {
      addToast({ tone: 'danger', message: e?.message || 'Billing mode update failed' });
    } finally { setBillingModeSaving(false); }
  };

  // Preview prerequisite auto-add whenever selection changes.
  useEffect(() => {
    if (!isOpen || !cid) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.validatePlan(cid, [...selected]);
        const data = res && res.data ? res.data : res;
        if (!cancelled) setAutoAdded((data?.autoAdded || []).filter((k) => !selected.has(k) && k !== 'CORE'));
      } catch (_) { /* preview is best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [selected, isOpen, cid]);

  // Build the editable quota map from the summary's quota rows whenever it loads.
  useEffect(() => {
    const map = {};
    (summary?.quotas || []).forEach((q) => {
      map[q.key] = { soft: q.soft != null ? String(q.soft) : '', hard: q.hard != null ? String(q.hard) : '' };
    });
    setQuotaEdits(map);
  }, [summary]);

  const toggle = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const applyEdition = (ed) => setSelected(new Set(ed.modules));

  const applyModules = async () => {
    // DEF-ENT-MIN-PLAN-001 — block saving an empty plan. CORE is always on and grants no
    // governed capability, so a plan with no module selected would leave the customer able
    // to log in but do nothing. The BE rejects this too (400); this is the friendly guard.
    if (selected.size === 0) {
      addToast({ tone: 'danger', message: 'Select at least one module before applying.' });
      return;
    }
    setSaving(true);
    try {
      const res = await api.replaceEntitlements(cid, { modules: [...selected], protectionMode: protection });
      if (res && res.ok === false) throw new Error(res.error || 'Update failed');
      addToast({ tone: 'success', message: 'Plan updated' });
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      addToast({ tone: 'danger', message: e?.message || 'Update failed' });
    } finally {
      setSaving(false);
    }
  };

  const saveServicePlan = async () => {
    setSaving(true);
    try {
      const res = await api.setServicePlan(cid, plan);
      if (res && res.ok === false) throw new Error(res.error || 'Update failed');
      addToast({ tone: 'success', message: 'Service plan updated' });
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      addToast({ tone: 'danger', message: e?.message || 'Update failed' });
    } finally {
      setSaving(false);
    }
  };

  const saveEnforcement = async (mode) => {
    setSaving(true);
    try {
      const res = await api.setEnforcement(cid, mode);
      if (res && res.ok === false) throw new Error(res.error || 'Update failed');
      addToast({ tone: mode === 'ENFORCE' ? 'warning' : 'success', message: `Enforcement: ${mode}` });
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      addToast({ tone: 'danger', message: e?.message || 'Update failed' });
    } finally {
      setSaving(false);
    }
  };

  // ── Billing & invoicing handlers (additive) ──
  const billingField = (k, v) => setBilling((b) => ({ ...(b || {}), [k]: v }));
  const billingDates = () => (billing && Array.isArray(billing.customBillingDates)) ? billing.customBillingDates : [];
  const addBillingDate = () => setBilling((b) => ({ ...(b || {}), customBillingDates: [...(((b || {}).customBillingDates) || []), ''] }));
  const setBillingDate = (i, v) => setBilling((b) => { const d = [...(((b || {}).customBillingDates) || [])]; d[i] = v; return { ...(b || {}), customBillingDates: d }; });
  const removeBillingDate = (i) => setBilling((b) => { const d = [...(((b || {}).customBillingDates) || [])]; d.splice(i, 1); return { ...(b || {}), customBillingDates: d }; });

  const saveBilling = async () => {
    setBillingSaving(true);
    try {
      const res = await api.updateBillingConfig(cid, {
        billingMode: billing?.billingMode,
        billingFrequency: billing?.billingFrequency,
        billingEmail: billing?.billingEmail || '',
        paymentTerms: billing?.paymentTerms,
        customBillingDates: billingDates().filter(Boolean),
        autoInvoicingEnabled: billing?.autoInvoicingEnabled,
      });
      setBilling(res && res.data ? res.data : res);
      addToast({ tone: 'success', message: 'Billing updated' });
    } catch (e) {
      addToast({ tone: 'danger', message: e?.message || 'Billing update failed' });
    } finally { setBillingSaving(false); }
  };

  // Friendly reasons for a not-sent invoice, so raw backend codes never reach the operator.
  const SEND_INVOICE_REASONS = {
    no_smtp_configured: 'the platform email relay isn’t set up yet — configure it in Admin Center → Platform Connectors',
    no_billing_email: 'no billing email is set for this customer',
    no_line_items: 'there’s nothing to invoice for this period yet',
    disabled: 'automated invoicing is turned off for this customer',
  };

  const doSendInvoice = async () => {
    setBillingSaving(true);
    try {
      const res = await api.sendInvoiceNow(cid);
      const data = res && res.data ? res.data : res;
      const reason = SEND_INVOICE_REASONS[data?.detail] || 'the invoice couldn’t be sent';
      addToast({ tone: data?.sent ? 'success' : 'warning',
        message: data?.sent ? `Invoice ${data.invoiceNumber} sent` : `Not sent — ${reason}.` });
    } catch (e) {
      addToast({ tone: 'danger', message: e?.message || 'Send failed' });
    } finally { setBillingSaving(false); }
  };

  const previewInvoice = () => { if (cid) window.open(api.invoicePreviewHtmlUrl(cid), '_blank', 'noopener'); };

  // ── Rate limits & quotas handlers (wired to existing /entitlements/quota endpoints) ──
  const setQuotaVal = (key, field, raw) =>
    setQuotaEdits((m) => ({ ...m, [key]: { ...(m[key] || {}), [field]: raw } }));

  const saveQuotas = async () => {
    setSaving(true);
    try {
      for (const q of QUOTAS) {
        const e = quotaEdits[q.key] || {};
        const soft = parseQty(e.soft);
        const hard = parseQty(e.hard);
        // Only persist rows the operator actually set (avoids creating empty/unlimited rows).
        if (soft != null || hard != null) {
          const res = await api.setQuota(cid, q.key, soft, hard);
          if (res && res.ok === false) throw new Error(res.error || 'Quota update failed');
        }
      }
      addToast({ tone: 'success', message: 'Quotas updated' });
      await load();
    } catch (e) {
      addToast({ tone: 'danger', message: e?.message || 'Quota update failed' });
    } finally { setSaving(false); }
  };

  const moduleState = (key) => summary?.modules?.[key] || 'DISABLED';

  // The customer's CURRENT (enforced) edition = the preset whose module set matches
  // the modules that are actually present (not DISABLED, CORE excluded). Derived from
  // the saved summary — not the in-editor `selected` — so it reflects what's live.
  // No match → the plan has been customized ("Custom").
  const presentModules = new Set(
    Object.entries(summary?.modules || {})
      .filter(([k, v]) => k !== 'CORE' && v !== 'DISABLED')
      .map(([k]) => k)
  );
  const currentEdition = summary
    ? (EDITIONS.find((ed) => ed.modules.length === presentModules.size
        && ed.modules.every((k) => presentModules.has(k))) || null)
    : null;

  const header = (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck size={14} className="text-v2-action-strong" />
        <Pill tone="action" size="sm">Plan & Entitlements</Pill>
      </div>
      <h2 className="text-[16px] font-bold tracking-tight m-0">{customer?.displayName || customer?.name}</h2>
      <p className="text-[12px] text-v2-ink-secondary mt-1 mb-0">
        Modules, Workflow Protection mode, and the non-functional Service Plan.
        {summary && (
          <span className={summary.enforced ? 'text-v2-danger' : 'text-v2-warning'}>
            {' '}Enforcement: {summary.enforcementMode || 'OBSERVE'}
            {summary.globalEnforce ? ' (global master ON)' : ''}.
          </span>
        )}
      </p>
    </div>
  );

  const footer = (
    <>
      {selected.size === 0 && (
        <span className="text-[11px] text-v2-danger mr-auto self-center">Select at least one module.</span>
      )}
      <Button variant="secondary" size="sm" onClick={onClose} disabled={saving}>Close</Button>
      <Button variant="primary" size="sm" onClick={applyModules} disabled={saving || loading || selected.size === 0}
        iconLeft={<Check size={13} />}>{saving ? 'Saving…' : 'Apply modules'}</Button>
    </>
  );

  return (
    <Slideout isOpen={isOpen} onClose={onClose} ariaLabel="Plan & Entitlements"
      header={header} footer={footer} width={640}>
      {loading && <div className="text-v2-ink-muted text-[13px]">Loading plan…</div>}

      {!loading && summary && (
        <div className="space-y-4">
          {/* Commercial Orders (Slice O5) — the source that drives the edition/modules/billing below.
            * onChanged reloads THIS panel's entitlements (Edition preset / Modules refresh after an
            * order is applied) rather than the customer list — so applying a line updates the sections
            * below in place, without re-fetching/refreshing the whole Customers table. */}
          <OrdersCard customer={customer} onChanged={load} />

          {/* Plan Enforcement — THE access gate. Distinct from Workflow Protection (M2). */}
          <Card padding="sm">
            <Card.Body>
              <div className="flex items-center justify-between mb-1">
                <div className="text-[12px] font-semibold text-v2-ink">Plan enforcement — gates module access</div>
                <Pill tone={enforcementMode === 'ENFORCE' ? 'danger' : 'warning'} size="sm" dot>
                  {enforcementMode === 'ENFORCE' ? 'ENFORCING' : 'OBSERVE'}
                </Pill>
              </div>
              <div className="text-[11px] text-v2-ink-muted mb-2">
                This is the switch that makes the plan take effect. Observe = log-only (nothing hidden or blocked).
                Enforce = this customer’s users see only entitled modules and un-entitled writes return 403. After
                flipping to Enforce, the customer’s admins must reload. (Separate from “Workflow Protection” below,
                which only protects the M2 runtime block.)
              </div>
              <div className="flex gap-2">
                {['OBSERVE', 'ENFORCE'].map((mode) => (
                  <Button key={mode} size="sm"
                    variant={enforcementMode === mode ? 'primary' : 'secondary'}
                    onClick={() => saveEnforcement(mode)} disabled={saving}>
                    {mode === 'ENFORCE' ? 'Enforce' : 'Observe'}
                  </Button>
                ))}
              </div>
            </Card.Body>
          </Card>

          {/* Edition presets */}
          <Card padding="sm">
            <Card.Body>
              <div className="flex items-center gap-2 mb-2">
                <div className="text-[12px] font-semibold text-v2-ink">Edition preset</div>
                <Pill tone="muted" size="sm">seeded by order</Pill>
                <span className="text-[11px] text-v2-ink-muted">·</span>
                <span className="text-[11px] text-v2-ink-muted">Current plan:</span>
                {currentEdition
                  ? <Pill tone="info" size="sm">{currentEdition.label}</Pill>
                  : <Pill tone="muted" size="sm">Custom</Pill>}
              </div>
              <div className="flex flex-wrap gap-2">
                {EDITIONS.map((ed) => {
                  const isCurrent = currentEdition?.key === ed.key;
                  return (
                    <Button key={ed.key} variant={isCurrent ? 'primary' : 'secondary'} size="sm"
                      onClick={() => applyEdition(ed)}
                      iconLeft={isCurrent ? <Check size={12} /> : undefined}
                      title={isCurrent ? 'This customer’s current enforced edition' : `Select the ${ed.label} module set`}>
                      {ed.label}
                    </Button>
                  );
                })}
              </div>
              <div className="text-[11px] text-v2-ink-muted mt-2">The highlighted preset is this customer’s current plan. Picking a preset selects its modules (prerequisites added automatically); click “Apply modules” to save.</div>
            </Card.Body>
          </Card>

          {/* Module toggles */}
          <Card padding="sm">
            <Card.Body>
              <div className="flex items-center gap-2 mb-2">
                <div className="text-[12px] font-semibold text-v2-ink">Modules</div>
                <Pill tone="muted" size="sm">seeded by order</Pill>
              </div>
              <div className="space-y-1.5">
                {MODULES.map((m) => {
                  const checked = selected.has(m.key);
                  const auto = autoAdded.includes(m.key);
                  return (
                    <label key={m.key} className="flex items-center gap-2 text-[12.5px] text-v2-ink cursor-pointer">
                      <input type="checkbox" checked={checked || auto} disabled={auto}
                        onChange={() => toggle(m.key)} />
                      <span className="flex-1">{m.label}</span>
                      {auto && <Pill tone="muted" size="sm">prereq — added</Pill>}
                      <Pill tone={STATE_TONE[moduleState(m.key)] || 'muted'} size="sm">{moduleState(m.key)}</Pill>
                    </label>
                  );
                })}
              </div>
            </Card.Body>
          </Card>

          {/* Workflow Protection mode */}
          <Card padding="sm">
            <Card.Body>
              <div className="text-[12px] font-semibold text-v2-ink mb-1">Workflow Protection — runtime gate (M2 only, not module access)</div>
              <div className="text-[11px] text-v2-ink-muted mb-2">Observe-only (shadow) evaluates + seals every verdict but never blocks. Enforce blocks on violation.</div>
              <div className="flex gap-2">
                {['OBSERVE_ONLY', 'ENTITLED'].map((mode) => (
                  <Button key={mode} size="sm"
                    variant={protection === mode ? 'primary' : 'secondary'}
                    onClick={() => setProtection(mode)}>
                    {mode === 'ENTITLED' ? 'Enforce' : 'Observe-only'}
                  </Button>
                ))}
              </div>
            </Card.Body>
          </Card>

          {/* Rate limits & quotas */}
          <Card padding="sm">
            <Card.Body>
              <div className="text-[12px] font-semibold text-v2-ink mb-1">Rate limits &amp; quotas</div>
              <div className="text-[11px] text-v2-ink-muted mb-2">
                Soft = warn, Hard = block / meter. Leave blank for unlimited.
                Tip: type <span className="font-mono">1k</span> / <span className="font-mono">1m</span> / <span className="font-mono">1b</span> for large values.
              </div>
              <table className="w-full text-[12px] table-fixed">
                <thead>
                  <tr className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider">
                    <th className="text-left font-semibold py-1 w-[34%]">Quota</th>
                    <th className="text-right font-semibold py-1 w-[27%] pr-1">Soft</th>
                    <th className="text-right font-semibold py-1 w-[27%] pr-1">Hard</th>
                    <th className="text-left font-semibold py-1 pl-2 w-[12%]">Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {QUOTAS.map((q) => {
                    const e = quotaEdits[q.key] || {};
                    return (
                      <tr key={q.key} className="border-t border-v2-border-subtle align-top">
                        <td className="py-1.5 text-v2-ink">{q.label}</td>
                        <td className="py-1.5 pr-1">
                          <input type="text" inputMode="numeric" className={`${selCls} w-full text-right`}
                            value={e.soft ?? ''} placeholder="—"
                            onChange={(ev) => setQuotaVal(q.key, 'soft', ev.target.value)} />
                          {usedShorthand(e.soft) && parseQty(e.soft) != null && (
                            <div className="text-[10px] text-v2-ink-muted text-right mt-0.5">= {fmtQty(parseQty(e.soft))}</div>
                          )}
                        </td>
                        <td className="py-1.5 pr-1">
                          <input type="text" inputMode="numeric" className={`${selCls} w-full text-right`}
                            value={e.hard ?? ''} placeholder="—"
                            onChange={(ev) => setQuotaVal(q.key, 'hard', ev.target.value)} />
                          {usedShorthand(e.hard) && parseQty(e.hard) != null && (
                            <div className="text-[10px] text-v2-ink-muted text-right mt-0.5">= {fmtQty(parseQty(e.hard))}</div>
                          )}
                        </td>
                        <td className="py-1.5 pl-2 text-v2-ink-muted">{q.unit}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="mt-2">
                <Button variant="secondary" size="sm" onClick={saveQuotas} disabled={saving}>Apply quotas</Button>
              </div>
            </Card.Body>
          </Card>

          {/* LLM billing mode — the customer contract (who pays for managed model usage) */}
          <Card padding="sm">
            <Card.Body>
              <div className="text-[12px] font-semibold text-v2-ink mb-1">LLM billing mode</div>
              <div className="text-[11px] text-v2-ink-muted mb-2">
                Sets how this customer is billed for Anthropic model usage.
                <span className="font-medium text-v2-ink"> Managed</span> — Prove7 provides the model and invoices
                usage (provider cost plus margin).
                <span className="font-medium text-v2-ink"> Bring Your Own Key (BYOK)</span> — the customer’s own
                connector key is used and their provider bills them directly, so usage is shown here for visibility
                but never invoiced.
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11.5px] text-v2-ink-secondary w-24">Anthropic</span>
                {[['MANAGED', 'Managed'], ['BYOK', 'Bring Your Own Key (BYOK)']].map(([m, l]) => (
                  <Button key={m} size="sm"
                    variant={(billingModes.anthropic || 'MANAGED') === m ? 'primary' : 'secondary'}
                    onClick={() => saveBillingMode('anthropic', m)} disabled={billingModeSaving}>
                    {l}
                  </Button>
                ))}
              </div>
            </Card.Body>
          </Card>

          {/* Billing & invoicing */}
          <Card padding="sm">
            <Card.Body>
              <div className="text-[12px] font-semibold text-v2-ink mb-1">Billing &amp; invoicing</div>

              <div className="text-[11px] text-v2-ink-muted mb-1">Billing mode</div>
              <div className="flex gap-2 mb-3">
                {[['PRE_COMMITTED', 'Pre-committed'], ['USAGE_BASED', 'Usage-based']].map(([k, l]) => (
                  <Button key={k} size="sm" variant={billing?.billingMode === k ? 'primary' : 'secondary'}
                    onClick={() => billingField('billingMode', k)}>{l}</Button>
                ))}
              </div>

              <div className="text-[11px] text-v2-ink-muted mb-1">Billing frequency</div>
              <div className="flex flex-wrap gap-2 mb-2">
                {[['MONTHLY', 'Monthly'], ['QUARTERLY', 'Quarterly'], ['YEARLY', 'Yearly'], ['CUSTOM', 'Custom']].map(([k, l]) => (
                  <Button key={k} size="sm" variant={billing?.billingFrequency === k ? 'primary' : 'secondary'}
                    onClick={() => billingField('billingFrequency', k)}>{l}</Button>
                ))}
              </div>

              {billing?.billingFrequency === 'CUSTOM' && (
                <div className="mb-3">
                  <div className="text-[11px] text-v2-ink-muted mb-1">Custom billing dates</div>
                  {billingDates().map((d, i) => (
                    <div key={i} className="flex items-center gap-2 mb-1">
                      <input type="date" className={`${selCls} flex-1`} value={d || ''}
                        onChange={(e) => setBillingDate(i, e.target.value)} />
                      <Button variant="secondary" size="sm" onClick={() => removeBillingDate(i)}>✕</Button>
                    </div>
                  ))}
                  <Button variant="secondary" size="sm" onClick={addBillingDate}>+ Add billing date</Button>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2 mb-3">
                <label className="text-[11px] text-v2-ink-muted">Payment terms
                  <select className={`${selCls} w-full mt-1`} value={billing?.paymentTerms || 'Net 30'}
                    onChange={(e) => billingField('paymentTerms', e.target.value)}>
                    {['Net 30', 'Net 45', 'Net 60', 'Net 90'].map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
                <label className="text-[11px] text-v2-ink-muted">Billing email
                  <input type="email" className={`${selCls} w-full mt-1`} value={billing?.billingEmail || ''}
                    onChange={(e) => billingField('billingEmail', e.target.value)} placeholder="billing@customer.com" />
                </label>
                <label className="text-[11px] text-v2-ink-muted">Invoice from
                  <input type="text" className={`${selCls} w-full mt-1`} value={billing?.invoiceFrom || 'billing@prove7.ai'} readOnly />
                </label>
              </div>

              <label className="flex items-center gap-2 text-[12.5px] text-v2-ink cursor-pointer mb-2">
                <input type="checkbox" checked={billing?.autoInvoicingEnabled ?? true}
                  onChange={(e) => billingField('autoInvoicingEnabled', e.target.checked)} />
                <span>Automated invoicing</span>
                {billing?.nextInvoiceAt && (
                  <Pill tone="muted" size="sm">Next: {String(billing.nextInvoiceAt).slice(0, 10)}</Pill>
                )}
              </label>

              <div className="flex flex-wrap gap-2 mt-1">
                <Button variant="primary" size="sm" onClick={saveBilling} disabled={billingSaving}>
                  {billingSaving ? 'Saving…' : 'Apply billing'}
                </Button>
                <Button variant="secondary" size="sm" onClick={previewInvoice} disabled={!cid}>Preview invoice</Button>
                <Button variant="secondary" size="sm" onClick={doSendInvoice} disabled={billingSaving}>Send test invoice</Button>
              </div>
            </Card.Body>
          </Card>

          {/* Pricing — rate card (PA price book; turns invoice "$—" into real numbers) */}
          <RateCardCard customerId={cid} />

          {/* Usage — current-month metered consumption per instance, with the managed LLM budget bar */}
          <UsageCard customerId={cid}
            budgetUsd={(summary?.quotas || []).find((q) => q.key === 'LLM_COST_USD')?.soft ?? null} />

          {/* Service plan */}
          <Card padding="sm">
            <Card.Body>
              <div className="text-[12px] font-semibold text-v2-ink mb-2">Service Plan</div>
              <div className="grid grid-cols-2 gap-2">
                <label className="text-[11px] text-v2-ink-muted">Support
                  <select className={`${selCls} w-full mt-1`} value={plan.supportTier}
                    onChange={(e) => setPlan({ ...plan, supportTier: e.target.value })}>
                    {SUPPORT.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
                <label className="text-[11px] text-v2-ink-muted">Availability
                  <select className={`${selCls} w-full mt-1`} value={plan.availabilityTier}
                    onChange={(e) => setPlan({ ...plan, availabilityTier: e.target.value })}>
                    {AVAIL.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
                <label className="text-[11px] text-v2-ink-muted">DR topology
                  <select className={`${selCls} w-full mt-1`} value={plan.drTopology}
                    onChange={(e) => setPlan({ ...plan, drTopology: e.target.value })}>
                    {DR.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
                <label className="text-[11px] text-v2-ink-muted">Regions
                  <input type="number" min={1} className={`${selCls} w-full mt-1`} value={plan.regions}
                    onChange={(e) => setPlan({ ...plan, regions: parseInt(e.target.value, 10) || 1 })} />
                </label>
              </div>
              <div className="mt-2">
                <Button variant="secondary" size="sm" onClick={saveServicePlan} disabled={saving}>Save service plan</Button>
              </div>
            </Card.Body>
          </Card>

          <div className="text-[11px] text-v2-ink-muted flex items-start gap-1.5">
            <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
            Removing a module winds it down to READ_ONLY (run-to-completion); it is not hard-deleted.
          </div>
        </div>
      )}
    </Slideout>
  );
}

export default EntitlementsPanel;
